import { useMemo, useState } from 'react';
import type { FieldPath, FieldValues } from 'react-hook-form';
import { useController, useFormContext } from 'react-hook-form';
import type { SxProps, Theme } from '@mui/material';
import { Menu, MenuItem, TextField } from '@mui/material';
import {
  FRACTIONS,
  type Fraction,
  formatFractionDisplay,
  formatStoredFraction,
  parseFractionInput,
  parseStoredFraction,
} from '../../../utils/fraction';

export interface RhfFractionFieldProps<T extends FieldValues> {
  name: FieldPath<T>;
  label: string;
  required?: boolean;
  placeholder?: string | null;
  sx?: SxProps<Theme>;
}

function RhfFractionField<T extends FieldValues>({
  name,
  label,
  required,
  placeholder,
  sx,
}: RhfFractionFieldProps<T>) {
  const { control } = useFormContext<T>();
  const {
    field,
    fieldState: { error },
  } = useController({
    control,
    name,
    rules: required ? { required: `${label} is required` } : undefined,
  });

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [menuWidth, setMenuWidth] = useState<number | undefined>();

  const { whole, fraction } = useMemo(
    () => parseStoredFraction(field.value ?? ''),
    [field.value]
  );

  return (
    <>
      <TextField
        {...field}
        value={formatFractionDisplay({ whole, fraction })}
        label={label}
        placeholder={placeholder ?? undefined}
        InputLabelProps={placeholder ? { shrink: true } : undefined}
        required={required}
        fullWidth
        sx={sx}
        size="small"
        error={!!error}
        helperText={error?.message}
        onChange={(e) => {
          const parsed = parseFractionInput(e.target.value);
          if (parsed === null) return;
          field.onChange(formatStoredFraction(parsed));
        }}
        slotProps={{
          input: {
            onKeyDown: (e) => {
              const el = e.currentTarget;

              if (e.key === ' ' || e.key === '.') {
                e.preventDefault();
                e.stopPropagation();
                setTimeout(() => {
                  setAnchorEl(el);
                  const control =
                    el.closest<HTMLElement>('.MuiFormControl-root') ?? el;
                  setMenuWidth(control.offsetWidth);
                }, 200);
                return;
              }

              if (e.key === 'Backspace' && fraction) {
                const pos = el.selectionStart ?? 0;
                const prev = el.value[pos - 1];
                if (prev === fraction) {
                  e.preventDefault();
                  field.onChange(
                    formatStoredFraction({ whole, fraction: null })
                  );
                }
              }
            },
          },
        }}
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => {
          setAnchorEl(null);
          setMenuWidth(undefined);
        }}
        PaperProps={{
          sx: { width: menuWidth },
        }}
      >
        {(Object.keys(FRACTIONS) as Fraction[]).map((fraction) => (
          <MenuItem
            key={fraction}
            onClick={() => {
              field.onChange(
                formatStoredFraction({ whole: whole ?? 0, fraction })
              );
              setAnchorEl(null);
            }}
          >
            {formatFractionDisplay({ whole, fraction })}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export default RhfFractionField;
